import React from "react";
import useActive from "../../../../hooks/useActive";
import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import RenderIf from "../../../Common/RenderIf/RenderIf";
import SeriesList from "./SeriesList";
import SeriesDetails from "./SeriesDetails";
import SeriesContent from "./SeriesContent";
import { useCreateElementContext } from "../Context/CreateElementContext";
import { isEmpty, isEqual } from "lodash-es";
import { ErrorLabel } from "../../ElementForm";
import { areAllSetsEqual } from "../../../../utils/sets";

const steps = ["Select series", "Add details", "Add content"];

const REQUIRED_MESSAGE = "This field is required";

const validations = {
  0: ({ values }) => {
    const errors = {};
    if (values.sequence_details.length < 2) {
      errors.sequence_details = "Please add at least 2 elements";
    } else if (
      new Set(values.sequence_details.map((item) => item.ref_id)).size !==
      values.sequence_details.length
    ) {
      errors.sequence_details = "Please add different elements";
    } else if (
      !areAllSetsEqual(
        values.sequence_details.map((item) => new Set(item.languages))
      )
    ) {
      errors.sequence_details =
        "Please add elements with same set of languages";
    }

    return errors;
  },
  1: ({ values }) => {
    const errors = {};
    if (!values?.thumbnail?.portrait)
      errors["thumbnail.portrait"] = REQUIRED_MESSAGE;
    if (!values?.thumbnail?.landscape)
      errors["thumbnail.landscape"] = REQUIRED_MESSAGE;
    if (!values?.priority) errors["priority"] = REQUIRED_MESSAGE;
    return errors;
  },
  2: ({ values }) => {
    const errors = {};
    (values?.content ?? []).forEach((item, index) => {
      const key = `content.${index}.`;
      if (!item.title) errors[key + "title"] = REQUIRED_MESSAGE;
      if (item?.title?.length > 50)
        errors[key + "title"] = "Length of title can be max 50";
      if (!item.description) errors[key + "description"] = REQUIRED_MESSAGE;
      if (item?.description?.length > 250)
        errors[key + "description"] = "Length of description can be max 250";
    });
    return errors;
  },
};

const SeriesForm = ({}) => {
  const { active, handleActive } = useActive(0);
  const { form, handleCreate, edit } = useCreateElementContext();

  const handleNext = () => {
    const validation = validations[active];

    const errors = validation({ values: form.values });

    if (isEmpty(errors)) {
      if (active === 0) {
        if (edit) {
          if (
            !isEqual(
              new Set(form.values.sequence_details[0].languages),
              new Set(form.values.content.map((item) => item.lang_code))
            )
          ) {
            const langAlreadyInContent = new Set(
              form.values.content.map((item) => item.lang_code)
            );
            form.setFields((prev) => ({
              content: form.values.sequence_details[0].languages.map((item) => {
                if (langAlreadyInContent.has(item)) {
                  return prev.content.find(
                    (content) => content.lang_code === item
                  );
                }
                return {
                  lang_code: item,
                  title: "",
                  description: "",
                };
              }),
            }));
          }
        } else {
          form.setFields({
            content: form.values.sequence_details[0].languages.map((item) => ({
              lang_code: item,
              title: "",
              description: "",
            })),
          });
        }
      }
      if (active === 2) {
        handleCreate();
      } else {
        handleActive((active) => active + 1);
      }
    }
    form.setErrors(errors);
  };
  const handleBack = () => {
    if (active > 0) handleActive((active) => active - 1);
  };

  const errorMessage =
    Object.values(form.errors).filter(Boolean).length > 1
      ? `${
          Object.values(form.errors).filter(Boolean).length
        } errors, please correct`
      : Object.values(form.errors).join(", ");

  return (
    <Box>
      <Stepper
        activeStep={active}
        sx={{
          maxWidth: "656px",
          width: "75%",
          my: 3,
          mx: "auto",
        }}
      >
        {steps.map((label, index) => {
          const stepProps = {};
          const labelProps = {};

          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Box sx={{ border: "1px solid #D6D6D6", p: 4, borderRadius: 2 }}>
        <RenderIf condition={active === 0}>
          <SeriesList />
        </RenderIf>
        <RenderIf condition={active === 1}>
          <SeriesDetails />
        </RenderIf>
        <RenderIf condition={active === 2}>
          <SeriesContent />
        </RenderIf>
      </Box>
      <Box
        mt={4}
        gap={2}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <ErrorLabel label={errorMessage} />
        <Box ml="auto" gap={2} display={"flex"}>
          <Button
            onClick={handleBack}
            //   disabled={isPending}
            color="error"
            variant="outlined"
          >
            Back
          </Button>
          <Button
            onClick={handleNext}
            //   disabled={isPending}
            color="error"
            variant="contained"
          >
            {active === 2 ? "Submit" : "Next"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SeriesForm;
